import React, { useCallback, useEffect, useState } from 'react';
import { Button, Card, Col, Row, Table } from '@themesberg/react-bootstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faArrowLeft, faSignOutAlt } from '@fortawesome/free-solid-svg-icons';
import { Link, useParams } from 'react-router-dom';
import PageHeader, { AlertMessage } from '../../components/PageHeader';
import AdminNav from '../../components/AdminNav';
import { useAdminAuth } from '../../context/AdminAuthContext';
import { adminApi } from '../../services/api';
import { Routes } from '../../routes';

function formatTs(value) {
  if (!value) return '—';
  try {
    return new Date(value).toLocaleString();
  } catch {
    return String(value);
  }
}

export default function AdminTenantDetail() {
  const { id } = useParams();
  const { username, logout, accessToken } = useAdminAuth();
  const [tenant, setTenant] = useState(null);
  const [loading, setLoading] = useState(true);
  const [acting, setActing] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  const load = useCallback(async () => {
    if (!accessToken || !id) return;
    setError('');
    setLoading(true);
    try {
      const { data } = await adminApi.getTenant(id);
      setTenant(data);
    } catch (err) {
      setError(err.message || 'Failed to load tenant');
      setTenant(null);
    } finally {
      setLoading(false);
    }
  }, [accessToken, id]);

  useEffect(() => {
    load();
  }, [load]);

  const handleDisable = async () => {
    if (!window.confirm('Suspend this tenant? They will not be able to obtain or use JWTs until re-enabled.')) {
      return;
    }
    setActing(true);
    setError('');
    setSuccess('');
    try {
      await adminApi.disableTenant(id);
      setSuccess('Tenant suspended.');
      await load();
    } catch (err) {
      setError(err.message || 'Disable failed');
    } finally {
      setActing(false);
    }
  };

  const handleEnable = async () => {
    setActing(true);
    setError('');
    setSuccess('');
    try {
      await adminApi.enableTenant(id);
      setSuccess('Tenant re-activated. They must sign in again for a new JWT.');
      await load();
    } catch (err) {
      setError(err.message || 'Enable failed');
    } finally {
      setActing(false);
    }
  };

  return (
    <>
      <PageHeader
        title={tenant ? tenant.name : 'Tenant'}
        subtitle={`Platform admin${username ? ` · ${username}` : ''}`}
      >
        <Button
          as={Link}
          to={Routes.AdminTenants.path}
          variant="outline-secondary"
          size="sm"
          className="me-2"
        >
          <FontAwesomeIcon icon={faArrowLeft} className="me-1" />
          Tenants
        </Button>
        <Button variant="outline-danger" size="sm" onClick={logout}>
          <FontAwesomeIcon icon={faSignOutAlt} className="me-1" />
          Sign out
        </Button>
      </PageHeader>

      <AdminNav />

      <AlertMessage variant="danger" message={error} onClose={() => setError('')} />
      <AlertMessage variant="success" message={success} onClose={() => setSuccess('')} />

      {loading ? (
        <p className="text-gray">Loading…</p>
      ) : !tenant ? (
        <p className="text-gray">Tenant not found.</p>
      ) : (
        <>
          <Card border="light" className="shadow-sm mb-4">
            <Card.Body>
              <Row className="mb-3">
                <Col md={6}>
                  <div className="text-gray small">Tenant ID</div>
                  <code>{tenant.id}</code>
                </Col>
                <Col md={3}>
                  <div className="text-gray small">Status</div>
                  <span
                    className={`badge bg-${tenant.status === 'ACTIVE' ? 'success' : 'secondary'}`}
                  >
                    {tenant.status}
                  </span>
                </Col>
                <Col md={3}>
                  <div className="text-gray small">Created</div>
                  <span className="small">{formatTs(tenant.createdAt)}</span>
                </Col>
              </Row>
              <Row className="mb-3">
                <Col md={4}>
                  <div className="text-gray small">Rules</div>
                  <strong>{tenant.ruleCount}</strong>
                </Col>
                <Col md={4}>
                  <div className="text-gray small">Blocklist entries</div>
                  <strong>{tenant.blocklistCount}</strong>
                </Col>
                <Col md={4}>
                  <div className="text-gray small">Tokens invalid before</div>
                  <span className="small">{formatTs(tenant.tokensInvalidBefore)}</span>
                </Col>
              </Row>
              <div>
                {tenant.status === 'ACTIVE' ? (
                  <Button variant="outline-danger" size="sm" disabled={acting} onClick={handleDisable}>
                    Suspend tenant
                  </Button>
                ) : (
                  <Button variant="outline-success" size="sm" disabled={acting} onClick={handleEnable}>
                    Re-activate tenant
                  </Button>
                )}
              </div>
            </Card.Body>
          </Card>

          <h6 className="mb-2">Velocity rules</h6>
          <Card border="light" className="shadow-sm mb-4">
            <Card.Body className="p-0">
              <Table responsive hover className="align-middle mb-0">
                <thead className="thead-light">
                  <tr>
                    <th>Name</th>
                    <th>Event</th>
                    <th>Aggregate</th>
                    <th>Group by</th>
                    <th>Threshold</th>
                    <th>Decision</th>
                    <th>Enabled</th>
                  </tr>
                </thead>
                <tbody>
                  {(tenant.rules || []).length === 0 ? (
                    <tr>
                      <td colSpan={7} className="text-center text-gray py-3">
                        No rules.
                      </td>
                    </tr>
                  ) : (
                    tenant.rules.map((r) => (
                      <tr key={r.id}>
                        <td className="fw-bold">{r.name}</td>
                        <td className="small">{r.eventType || '—'}</td>
                        <td className="small">{r.aggregate}</td>
                        <td>
                          <code className="small">{r.groupBy}</code>
                        </td>
                        <td className="small">
                          {r.threshold} / {r.window || '—'}
                        </td>
                        <td className="small">{r.decision}</td>
                        <td>
                          <span className={`badge bg-${r.enabled ? 'success' : 'secondary'}`}>
                            {r.enabled ? 'yes' : 'no'}
                          </span>
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </Table>
            </Card.Body>
          </Card>

          <h6 className="mb-2">Blocklist</h6>
          <Card border="light" className="shadow-sm mb-4">
            <Card.Body className="p-0">
              <Table responsive hover className="align-middle mb-0">
                <thead className="thead-light">
                  <tr>
                    <th>Type</th>
                    <th>Value</th>
                    <th>Reason</th>
                    <th>Expires</th>
                    <th>Created</th>
                  </tr>
                </thead>
                <tbody>
                  {(tenant.blocklist || []).length === 0 ? (
                    <tr>
                      <td colSpan={5} className="text-center text-gray py-3">
                        No blocklist entries.
                      </td>
                    </tr>
                  ) : (
                    tenant.blocklist.map((b) => (
                      <tr key={b.id}>
                        <td className="small">{b.subjectType}</td>
                        <td>
                          <code className="small">{b.subjectValue}</code>
                        </td>
                        <td className="small text-gray">{b.reason || '—'}</td>
                        <td className="small">{formatTs(b.expiresAt)}</td>
                        <td className="small text-gray">{formatTs(b.createdAt)}</td>
                      </tr>
                    ))
                  )}
                </tbody>
              </Table>
            </Card.Body>
          </Card>
        </>
      )}
    </>
  );
}
